import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/components/AuthProvider";
import { Pixel } from "@/types/database";
import PixelsSidebar from "@/components/dashboard/PixelsSidebar";
import VisitorsTable from "@/components/dashboard/VisitorsTable";
import { EmptyState } from "@/components/dashboard/EmptyState";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { usePixels } from "@/hooks/usePixels";
import { toast } from "sonner";

const Dashboard = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPixel, setSelectedPixel] = useState<Pixel | null>(null);

  useEffect(() => {
    document.title = "Dashboard | Lead Extract";
  }, []);

  const { 
    data: pixels, 
    error: pixelsError, 
    refetch: refetchPixels,
    isLoading: pixelsLoading
  } = usePixels(user?.id);
  
  const { 
    data: subscriptionStatus, 
    refetch: refetchSubscription, 
    isLoading: isSubscriptionLoading
  } = useSubscriptionStatus(user?.id);

  const handlePixelSelect = useCallback((pixel: Pixel | null) => {
    setSelectedPixel(pixel);
  }, []);

  useEffect(() => {
    if (pixels && pixels.length > 0) {
      if (!selectedPixel || !pixels.some(p => p.id === selectedPixel.id)) {
        setSelectedPixel(pixels[0]);
      }
    } else if (pixels && pixels.length === 0 && selectedPixel) {
      setSelectedPixel(null);
    }
  }, [pixels, selectedPixel]);

  useEffect(() => {
    if (user && window.$productFruits) {
      const userEmail = user.email || '';
      window.$productFruits.push(['init', '6ZAALmLKgEOzFMFS', 'en', { username: userEmail }]);
    }
  }, [user]);

  useEffect(() => {
    if (subscriptionStatus) {
      refetchPixels();
    }
  }, [subscriptionStatus, refetchPixels]);

  useEffect(() => {
    if (user?.id) {
      const subscriptionInterval = setInterval(() => {
        refetchSubscription();
      }, 30000);
      
      const pixelsInterval = setInterval(() => {
        refetchPixels();
      }, 30000); 
      
      return () => {
        clearInterval(subscriptionInterval);
        clearInterval(pixelsInterval);
      };
    }
  }, [user?.id, refetchSubscription, refetchPixels]);

  useEffect(() => {
    if (user?.id) {
      refetchPixels();
      
      const timer1 = setTimeout(() => {
        refetchPixels();
      }, 500);
      
      const timer2 = setTimeout(() => {
        refetchPixels();
      }, 1500);
      
      const timer3 = setTimeout(() => {
        refetchPixels();
      }, 3000);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [user?.id, refetchPixels]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.has('upgrade_success')) {
      toast.success('Your subscription has been upgraded successfully with prorated billing!');
      window.history.replaceState({}, document.title, window.location.pathname);
      refetchSubscription();
    }
    
    if (urlParams.has('success')) {
      toast.success('Subscription update successful!');
      window.history.replaceState({}, document.title, window.location.pathname);
      refetchSubscription();
    }
    
    if (urlParams.has('canceled')) {
      toast.info('Subscription update was canceled');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [refetchSubscription]);

  if (pixelsError?.message === 'trial_required') {
    return <EmptyState hasPixels={false} />;
  }

  if (pixelsError) {
    toast.error(`Error loading pixels: ${pixelsError.message}`);
    return <div className="text-red-500">Error loading pixels: {pixelsError.message}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="flex">
        <PixelsSidebar
          pixels={pixels}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onPixelSelect={handlePixelSelect}
          selectedPixel={selectedPixel}
        />
        <div className="flex-1 pl-6 pr-6 pt-6 overflow-hidden w-0">
          {pixelsLoading ? (
            <div className="text-center py-8 text-gray-600">
              Loading pixels...
            </div>
          ) : !pixels || pixels.length === 0 ? (
            <EmptyState 
              hasPixels={false}
            />
          ) : !selectedPixel ? (
            <div className="text-center py-8 text-gray-600">
              Select a pixel from the sidebar to view its data
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-center p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-800">{selectedPixel.name}</h2>
                  <p className="text-sm text-gray-500 mt-1">{selectedPixel.domain}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 overflow-hidden">
                <VisitorsTable pixelId={selectedPixel.id} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
